import React, { useLayoutEffect, useRef, useState } from 'react';
import { useGsapTimeline } from 'animations/useGsapTimeline';
import { useAnimationActivity } from 'contexts/AnimationActivityContext';
import gsap from 'gsap';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useTableSnap } from 'hooks/useTableSnap';

import PokerChip from './PokerChip';

type ReturnedBetChipsPropType = {
  /** Seat the chips return to (the player's stack). */
  to: SeatPosition;
};

const CHIP_COUNT = 6;
const CHIP_SIZE = 14;

/**
 * Chips fly from the pot back to the player's seat, used when an over-bet
 * (uncalled portion of a bet) is returned to the player before showdown.
 * Mirrors WinChips (pot → seat) with its own visual treatment.
 *
 * Mount with a fresh key on each over-bet return so the animation re-fires.
 */
const ReturnedBetChips = ({ to }: ReturnedBetChipsPropType) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { createTimeline } = useGsapTimeline();
  const { begin } = useAnimationActivity();
  const shouldSnap = useTableSnap();
  const [origin, setOrigin] = useState<{ leftPct: number; topPct: number } | null>(null);
  // This component is NOT remounted for the rest of the session once an
  // over-bet return fires for this seat (Bet.tsx's `returnedChipFlightId`
  // only ever increments, it never resets, so the SAME instance just sits
  // there with its key unchanged). Its flight effect below necessarily
  // depends on `to`/`origin` to resolve the travel vector once — but with no
  // further guard, ANY later reason for `to` (seatPosition) to change, most
  // notably a POV-driven seat-ring rotation, re-runs the effect and replays
  // the entire flight for an over-bet that was already returned, with no
  // relation to any actual new event. WinChips/BetChips avoid this whole class
  // of bug by depending on nothing (mount-once); this effect can't do that
  // directly since `origin` resolves asynchronously, so instead: latch the
  // first successful run and refuse every one after it.
  const hasFiredRef = useRef(false);

  useLayoutEffect(() => {
    // Resolve the pot's position as a percentage of this component's OWN
    // table root, so the container can be placed using the same coordinate
    // system as seats/bets. closest(), never document.querySelector: the grid
    // mounts one root per cell (plus kept-alive panels), and the document's
    // FIRST root can be another table — or a hidden pot-less panel, which
    // would bail out below and silently skip the flight. Same rule as the
    // travel-vector effect further down and every other floating measurement
    // (EN-10567; dev review #3760). Requires the container div to exist on
    // first render — see the JSX note below.
    const tableEl = containerRef.current?.closest('[data-table-root]') as HTMLElement | null;
    const potEl = tableEl?.querySelector('[data-pot-target]') as HTMLElement | null;
    if (!tableEl || !potEl) return;

    const tableRect = tableEl.getBoundingClientRect();
    const potRect = potEl.getBoundingClientRect();
    if (tableRect.width === 0 || tableRect.height === 0) return;

    const potCenterX = potRect.left + potRect.width / 2 - tableRect.left;
    const potCenterY = potRect.top + potRect.height / 2 - tableRect.top;

    setOrigin({
      leftPct: (potCenterX / tableRect.width) * 100,
      topPct: (potCenterY / tableRect.height) * 100
    });
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || !origin) return;
    // Already flew (or snapped) once for this mount — a later `to`/`origin`
    // change (e.g. a seat rotation moving this seat) must not replay it.
    if (hasFiredRef.current) return;

    const tableEl = container.closest('[data-table-root]') as HTMLElement | null;
    if (!tableEl) return;

    // Travel vector: pot center → seat position, in pixels.
    const tableRect = tableEl.getBoundingClientRect();
    const dx = ((to.leftPct - origin.leftPct) / 100) * tableRect.width;
    const dy = ((to.topPct - origin.topPct) / 100) * tableRect.height;

    const chips = container.querySelectorAll('.returned-bet-chip');
    if (chips.length === 0) return;

    // Past every "not ready yet" bail — this mount gets exactly one real
    // attempt, snapped or animated, from here on.
    hasFiredRef.current = true;

    // Snapping (table switch / resync): the over-bet was already returned —
    // park the chips at the flight's end state instead of replaying it.
    if (shouldSnap()) {
      gsap.set(chips, { opacity: 0 });
      return;
    }

    // Mark the table busy for the flight so the seat rotation holds off.
    const endActivity = begin();

    const scatter = 14;
    const delayStep = 0.04;
    const arcBase = 20;
    const arcRand = 15;
    const burstFraction = 0.2;
    const burstScale = 1.2;
    const flyDuration = 0.45;
    const burstDuration = 0.18;
    const landDelay = burstDuration + flyDuration;

    const tl = createTimeline();
    tl.eventCallback('onComplete', endActivity);

    chips.forEach((chip, i) => {
      const sx = (Math.random() - 0.5) * scatter;
      const sy = (Math.random() - 0.5) * scatter;
      const delay = i * delayStep;
      const arcY = -(arcBase + Math.random() * arcRand);
      const initRot = Math.random() * 30 - 15;
      const burstRot = Math.random() * 80 - 40;

      tl.set(chip, { x: 0, y: 0, opacity: 0, scale: 0.5, rotation: initRot }, 0);

      // Lift off the pot
      tl.to(
        chip,
        {
          x: dx * burstFraction + sx,
          y: arcY,
          opacity: 1,
          scale: burstScale,
          rotation: burstRot,
          duration: burstDuration,
          ease: 'power2.out',
          delay
        },
        0
      )
        // Fly back to the seat
        .to(
          chip,
          {
            x: dx + sx,
            y: dy + sy,
            scale: 0.85,
            rotation: 0,
            duration: flyDuration,
            ease: 'power2.in'
          },
          burstDuration + delay
        )
        // Land and fade
        .to(
          chip,
          {
            opacity: 0,
            scale: 0.5,
            duration: 0.15,
            ease: 'power1.in'
          },
          landDelay + delay
        );
    });

    return () => endActivity();
  }, [origin, to?.leftPct, to?.topPct, createTimeline, begin]);

  if (!to) return null;

  return (
    // The container renders from the first paint (chips gated on `origin`
    // below, so nothing is visible yet) — the origin effect above needs the
    // ref populated to find this component's own table root via closest().
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        left: origin ? `${origin.leftPct}%` : 0,
        top: origin ? `${origin.topPct}%` : 0,
        // Same plane as the static bet pill and the dealer button (z5) — see
        // Bet.tsx BaseStyle for the 8/13 z-order model.
        zIndex: 5,
        pointerEvents: 'none'
      }}>
      {origin &&
        Array.from({ length: CHIP_COUNT }).map((_, i) => (
          <div key={i} className='returned-bet-chip' style={{ position: 'absolute' }}>
            <PokerChip size={CHIP_SIZE} color='red' />
          </div>
        ))}
    </div>
  );
};

export default ReturnedBetChips;
